import {
  ChangeEvent,
  Children,
  cloneElement,
  createRef,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { getNavigationOptions } from '@components/input/PinGroup';
import { Stack, StackProps } from '@components/layout';
import { Radio, RadioProps } from '../Radio';

export interface RadioGroupProps extends StackProps {
  /** radio buttons to group together */
  children: ReactElement[];
  /** callback function when radio button is changed */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** additional class name for the group wrapper */
  className?: string;
}

/**
 * RadioGroup component
 * @param children - radio buttons to group together
 * @param onChange - callback function when radio button is changed
 * @param className - additional class name for the group wrapper
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
  children,
  onChange,
  className,
  ...rest
}) => {
  const [value, setValue] = useState<string>('');
  const [childrenWithProps, setChildrenWithProps] = useState<ReactElement[]>([]);
  const inputRefs = useMemo(
    () => Array.from({ length: children.length }, () => createRef<HTMLInputElement>()),
    [children.length],
  );

  useEffect(() => {
    const areValidElements = (Children.toArray(children) as ReactElement[]).every(
      child => typeof child === 'object' && child.type === Radio,
    );

    if (areValidElements) {
      const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (onChange) onChange(e);
      };

      const handleRadioKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.code !== 'Tab') {
          e.preventDefault();
          e.stopPropagation();
        }

        if (e.code === 'ArrowDown' || e.code === 'ArrowRight') {
          const { nextInput } = getNavigationOptions({ refs: inputRefs, target: e.target });
          if (nextInput) nextInput.focus();
        }

        if (e.code === 'ArrowUp' || e.code === 'ArrowLeft') {
          const { previousInput } = getNavigationOptions({ refs: inputRefs, target: e.target });
          if (previousInput) previousInput.focus();
        }
      };

      const radioChildren = (children as ReactElement<RadioProps>[]).map(
        (child: ReactElement<RadioProps>, index) =>
          cloneElement(child, {
            onChange: handleRadioChange,
            onKeyDown: handleRadioKeyDown,
            onFocus: () => setValue(child.props.value),
            checked: value === child.props.value,
            inputRef: inputRefs[index],
          }),
      );

      setChildrenWithProps(radioChildren);
    } else {
      throw new Error("RadioGroup's children must be valid React elements of type Radio");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omitting onChange
  }, [value, children, inputRefs]);

  return (
    <Stack
      className={classNames('vds-radio-group', className)}
      {...rest}
      id={rest.id || ''}
      role={rest.role || 'radiogroup'}
      aria-label={rest['aria-label'] || ''}
      aria-labelledby={rest['aria-labelledby'] || `${rest.id}-label`}
      aria-describedby={rest['aria-describedby'] || `${rest.id}-description`}
      aria-required={rest['aria-required'] || false}
      aria-errormessage={rest['aria-errormessage'] || ''}>
      {childrenWithProps}
    </Stack>
  );
};
